import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Layers, Loader2, PlayCircle, CheckCircle, AlertTriangle, XCircle,
  FileStack, RefreshCw, Clock, Image as ImageIcon, ListChecks,
  Film, Download, RotateCw, TrendingUp, Gauge, SpellCheck,
} from 'lucide-react';

// BATCH-LI17-v1 — the 17-link LinkedIn base set (chronological order applied server-side).
// day_theme left '' where ambiguous → server auto-detects from the scraped title.
const BATCH_LI17 = [
  { d: '2026-06-02', label: 'June 2, 2026', t: 'Teach Me Tuesdays', u: 'https://www.linkedin.com/pulse/yahffirmations-day-reflections-teach-me-tuesdays-justice-cherise-t-wyvbc/' },
  { d: '2026-05-17', label: 'May 17, 2026', t: 'Siren Sundayz', u: 'https://www.linkedin.com/pulse/yahffirmations-day-reflections-siren-sundayz-red-light-cherise-t-cr59e/' },
  { d: '2026-05-05', label: 'May 5, 2026', t: 'Teach Me Tuesdays', u: 'https://www.linkedin.com/pulse/yahffirmations-day-reflections-teach-me-tuesdays-temple-cherise-t-tleke/' },
  { d: '2026-04-27', label: 'April 27, 2026', t: 'Magnificent New Dawn Mondays', u: 'https://www.linkedin.com/pulse/yahffirmations-day-reflections-magnifecent-new-dawn-mondays-cherise-t-vv01e/' },
  { d: '2026-04-20', label: 'April 20, 2026', t: 'Magnificent New Dawn Mondays', u: 'https://www.linkedin.com/pulse/yahffirmations-day-reflections-magnifecent-new-dawn-mondays-cherise-t-qc4oe/' },
  { d: '2026-04-08', label: 'April 8, 2026', t: 'Wisdom Wednesdays', u: 'https://www.linkedin.com/pulse/yahffirmations-day-reflections-wisdom-wednesdays-temple-cherise-t-9xtqe/' },
  { d: '2026-01-01', label: 'January 1, 2026', t: 'Thankful Thursdays', u: 'https://www.linkedin.com/pulse/yahffirmations-new-calendar-year-relections-thankful-thursdays-t-pvdne/' },
  { d: '2025-11-24', label: 'November 24, 2025', t: '', u: 'https://www.linkedin.com/pulse/yahffirmations-day-relections-magnificent-new-dawn-fly-cherise-t-al7gc/' },
  { d: '2025-11-03', label: 'November 3, 2025', t: '', u: 'https://www.linkedin.com/pulse/inspiyah-mail-mind-unplugged-from-blog-magnificent-new-cherise-t-cqonc/' },
  { d: '2025-10-13', label: 'October 13, 2025', t: '', u: 'https://www.linkedin.com/pulse/inspiyah-mail-mind-unplugged-from-blog-magnificent-new-cherise-t-m06oc/' },
  { d: '2025-08-10', label: 'August 10, 2025', t: 'Siren Sundayz', u: 'https://www.linkedin.com/pulse/inspiyah-mail-mind-unplugged-from-blog-siren-sundayz-smooth-cherise-t-hrg7c/' },
  { d: '2024-12-26', label: 'December 26, 2024', t: 'Thankful Thursdays', u: 'https://www.linkedin.com/pulse/inspiyah-mail-mind-unplugged-from-blog-episode-24-thankful-cherise-t-xjdoc/' },
  { d: '2026-07-26', label: 'July 26, 2026', t: 'Thankful Thursdays', u: 'https://www.linkedin.com/pulse/understanding-value-inspiyah-thankful-thursdays-elements-cherise-t-9ycfc/' },
  { d: '2024-07-19', label: 'July 19, 2024', t: 'Thankful Thursdays', u: 'https://www.linkedin.com/pulse/understanding-value-inspiyah-thankful-thursdays-alpha-cherise-t-blkoc/' },
  { d: '2024-06-12', label: 'June 12, 2024', t: '', u: 'https://www.linkedin.com/pulse/recognize-identify-shadows-forward-thinking-external-cherise-t-qdzac/' },
  { d: '2024-06-11', label: 'June 11, 2024', t: '', u: 'https://www.linkedin.com/pulse/thought-day-forward-thinking-brighter-tomorrow-seeds-inspiration-t-t9khc/' },
  { d: '2024-06-08', label: 'June 8, 2024', t: '', u: 'https://www.linkedin.com/pulse/inspiyah-mail-mind-unplugged-from-blog-magnificent-new-cherise-t-vj47c/' },
];

const STATUS_STYLES = {
  complete: 'bg-green-100 text-green-700',
  partial: 'bg-yellow-100 text-yellow-700',
  stub: 'bg-red-100 text-red-700',
};

const CompletenessPill = ({ status }) => (
  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
    {status || '—'}
  </span>
);

const readinessColor = (r) => {
  if (r == null) return 'bg-gray-100 text-gray-500';
  if (r >= 85) return 'bg-green-100 text-green-700';
  if (r >= 60) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
};

const ReadinessPill = ({ value }) => (
  <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${readinessColor(value)}`}>
    {value == null ? '—' : `${value}%`}
  </span>
);

const EntryRow = ({ entry, index }) => {
  const missing = entry.missing_required || [];
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50" data-testid={`batch-entry-row-${index}`}>
      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{entry.original_date || '—'}</td>
      <td className="px-3 py-2 text-sm text-gray-800 max-w-xs">
        <div className="truncate" title={entry.title || entry.url}>{entry.title || entry.url}</div>
        {entry.day_theme && <span className="text-[11px] text-purple-600">{entry.day_theme}</span>}
      </td>
      <td className="px-3 py-2 text-center">
        {entry.status === 'pending' && <Clock className="text-gray-400 mx-auto" size={16} />}
        {entry.status === 'done' && <CheckCircle className="text-green-500 mx-auto" size={16} />}
        {entry.status === 'error' && (
          <span title={entry.error}><XCircle className="text-red-500 mx-auto" size={16} /></span>
        )}
      </td>
      <td className="px-3 py-2 text-center text-sm text-gray-700">{entry.word_count ?? '—'}</td>
      <td className="px-3 py-2 text-center text-sm text-gray-700">{entry.image_count ?? '—'}</td>
      <td className="px-3 py-2 text-center text-sm text-gray-700">{entry.video_count ?? '—'}</td>
      <td className="px-3 py-2 text-center text-sm text-gray-700">{entry.spelling_errors ?? '—'}</td>
      <td className="px-3 py-2 text-center"><CompletenessPill status={entry.completeness_status} /></td>
      <td className="px-3 py-2 text-center"><ReadinessPill value={entry.readiness} /></td>
      <td className="px-3 py-2 text-xs text-gray-500 max-w-[10rem]">
        {entry.status === 'done'
          ? (missing.length ? <span className="text-yellow-700">missing: {missing.join(', ')}</span> : <span className="text-green-600">all required present</span>)
          : (entry.status === 'error' ? <span className="text-red-600 truncate block" title={entry.error}>{entry.error}</span> : '—')}
        {entry.is_duplicate && <span className="ml-1 text-amber-600">· re-ingested</span>}
      </td>
    </tr>
  );
};

const BatchIngestLane = () => {
  const [text, setText] = useState('');
  const [batchId, setBatchId] = useState('');
  const [batch, setBatch] = useState(null);
  const [starting, setStarting] = useState(false);
  const [batches, setBatches] = useState([]);
  const [reauditing, setReauditing] = useState(false);
  const [spellDownloading, setSpellDownloading] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchBatches();
    return () => clearInterval(pollRef.current);
  }, []);

  const fetchBatches = async () => {
    try {
      const res = await axios.get('/api/admin/import/batch/list');
      setBatches(res.data || []);
    } catch (e) { /* noop */ }
  };

  const loadDataset = () => {
    setText(BATCH_LI17.map((r) => `${r.d} | ${r.u}`).join('\n'));
    setBatchId('BATCH-LI17-v1');
  };

  // Parse textarea lines: "YYYY-MM-DD | url" or just "url".
  const parseEntries = () => {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    return lines.map((line) => {
      const parts = line.split('|').map((p) => p.trim());
      let sortKey = null, url = line, original = null, day_theme = '';
      if (parts.length >= 2 && /^\d{4}-\d{2}-\d{2}$/.test(parts[0])) {
        sortKey = parts[0];
        url = parts.slice(1).join('|').trim();
        const ds = BATCH_LI17.find((r) => r.d === sortKey && r.u === url);
        original = ds ? ds.label : new Date(sortKey).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        day_theme = ds ? ds.t : '';
      } else {
        const ds = BATCH_LI17.find((r) => r.u === url);
        if (ds) { sortKey = ds.d; original = ds.label; day_theme = ds.t; }
      }
      return { url, sort_key: sortKey, original_date: original, day_theme };
    });
  };

  const startBatch = async () => {
    const entries = parseEntries();
    if (entries.length === 0) { alert('Add at least one URL.'); return; }
    setStarting(true);
    setBatch(null);
    try {
      const res = await axios.post('/api/admin/import/batch/ingest', {
        entries,
        batch_id: batchId || null,
      });
      startPolling(res.data.id);
      fetchBatches();
    } catch (e) {
      console.error(e);
      alert('Failed to start batch ingest.');
    } finally {
      setStarting(false);
    }
  };

  const startPolling = (id) => {
    clearInterval(pollRef.current);
    const poll = async () => {
      try {
        const res = await axios.get(`/api/admin/import/batch/${id}`);
        setBatch(res.data);
        if (res.data.status === 'complete') {
          clearInterval(pollRef.current);
          fetchBatches();
        }
      } catch (e) { /* noop */ }
    };
    poll();
    pollRef.current = setInterval(poll, 2500);
  };

  const summary = () => {
    if (!batch) return null;
    const done = batch.entries.filter((e) => e.status === 'done');
    const errors = batch.entries.filter((e) => e.status === 'error').length;
    const byStatus = done.reduce((a, e) => { a[e.completeness_status] = (a[e.completeness_status] || 0) + 1; return a; }, {});
    const totalWords = done.reduce((a, e) => a + (e.word_count || 0), 0);
    const totalSpell = done.reduce((a, e) => a + (e.spelling_errors || 0), 0);
    const totalVideos = done.reduce((a, e) => a + (e.video_count || 0), 0);
    const totalImages = done.reduce((a, e) => a + (e.image_count || 0), 0);
    return { done: done.length, errors, byStatus, totalWords, totalSpell, totalVideos, totalImages };
  };
  const s = summary();

  const reaudit = async () => {
    if (!batch) return;
    setReauditing(true);
    try {
      await axios.post(`/api/admin/import/batch/${batch.id}/reaudit`);
      const res = await axios.get(`/api/admin/import/batch/${batch.id}`);
      setBatch(res.data);
    } catch (e) {
      console.error(e);
      alert('Re-audit failed.');
    } finally {
      setReauditing(false);
    }
  };

  const downloadCsv = () => {
    if (!batch) return;
    const a = document.createElement('a');
    a.href = `/api/admin/import/batch/${batch.id}/csv`;
    a.download = `${batch.batch_id}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Task C — per-flag spelling report (word → article_id). Endpoint re-runs
  // spellcheck so it can take ~1min; fetch as blob with a spinner.
  const downloadSpellingCsv = async () => {
    if (!batch) return;
    setSpellDownloading(true);
    try {
      const res = await axios.get(`/api/admin/import/batch/${batch.id}/spelling-csv`, {
        responseType: 'blob',
        timeout: 180000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${batch.batch_id}-spelling-flags.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert('Spelling CSV export failed.');
    } finally {
      setSpellDownloading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="batch-ingest-lane">
      {/* Header + Info */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
            <FileStack className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Batch Ingest <span className="text-sm font-normal text-gray-400">— faithful raw capture</span></h3>
            <p className="text-sm text-gray-500">Chronological (oldest → newest). No spellcheck, no templates — that's the Refine step.</p>
          </div>
        </div>

        <div className="mt-3 flex items-start space-x-2 bg-indigo-50 border border-indigo-100 rounded-lg p-3">
          <Layers className="text-indigo-500 flex-shrink-0 mt-0.5" size={16} />
          <p className="text-xs text-indigo-800">
            Ingest writes an immutable <code className="bg-white px-1 rounded">raw_scrape</code>, detects the Component Map
            (present vs missing), and flags completeness (<b>complete / partial / stub</b>). Nothing is normalized — historical
            quirks are preserved for the separate Refine pass.
          </p>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <input
            type="text"
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
            placeholder="Batch label (e.g. BATCH-LI17-v1)"
            className="w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900"
            data-testid="batch-label-input"
          />
          <button
            onClick={loadDataset}
            className="px-3 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium flex items-center gap-1.5"
            data-testid="load-dataset-btn"
          >
            <ListChecks size={16} /> Load 17-link set
          </button>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows="8"
          placeholder={'One per line — "YYYY-MM-DD | https://…" or just the URL'}
          className="w-full mt-3 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-xs text-gray-900"
          data-testid="batch-urls-textarea"
        />

        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-gray-500">{text.split('\n').filter((l) => l.trim()).length} URL(s) queued</span>
          <button
            onClick={startBatch}
            disabled={starting || (batch && batch.status === 'processing')}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center space-x-2"
            data-testid="start-batch-btn"
          >
            {starting ? <Loader2 className="animate-spin" size={20} /> : <PlayCircle size={20} />}
            <span>Start Batch Ingest</span>
          </button>
        </div>
      </div>

      {/* Live batch progress */}
      {batch && (
        <div className="bg-white rounded-xl shadow-lg p-6" data-testid="batch-progress-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              {batch.status === 'processing'
                ? <Loader2 className="animate-spin text-indigo-500" size={20} />
                : <CheckCircle className="text-green-500" size={20} />}
              <h4 className="text-lg font-bold text-gray-900">{batch.batch_id}</h4>
              <span className="text-sm text-gray-500">{batch.processed}/{batch.total} processed</span>
            </div>
            <div className="flex items-center gap-2">
              {batch.status === 'complete' && (
                <>
                  <button
                    onClick={downloadCsv}
                    className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium flex items-center gap-1.5"
                    data-testid="batch-csv-btn"
                  >
                    <Download size={14} /> CSV
                  </button>
                  <button
                    onClick={downloadSpellingCsv}
                    disabled={spellDownloading}
                    className="px-3 py-1.5 text-xs rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 font-medium flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="batch-spelling-csv-btn"
                    title="Per-flag spelling report: each flagged word mapped to its article_id (~1 min)"
                  >
                    {spellDownloading ? <Loader2 className="animate-spin" size={14} /> : <SpellCheck size={14} />}
                    {spellDownloading ? 'Building…' : 'Spelling CSV'}
                  </button>
                  <button
                    onClick={reaudit}
                    disabled={reauditing}
                    className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-medium flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="batch-reaudit-btn"
                  >
                    {reauditing ? <Loader2 className="animate-spin" size={14} /> : <RotateCw size={14} />} Re-Audit
                  </button>
                </>
              )}
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${batch.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'}`}>
                {batch.status}
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div className="h-2 rounded-full bg-indigo-500 transition-all" style={{ width: `${(batch.processed / batch.total) * 100}%` }} />
          </div>

          {/* Summary chips */}
          {s && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="batch-summary">
              <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium">{s.done} ingested</span>
              {batch.latest_readiness != null && (
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1 ${readinessColor(batch.latest_readiness)}`} data-testid="batch-readiness-chip">
                  <Gauge size={12} /> {batch.latest_readiness}% ready (avg)
                </span>
              )}
              {s.errors > 0 && <span className="text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-medium flex items-center gap-1"><AlertTriangle size={12} /> {s.errors} errors</span>}
              {Object.entries(s.byStatus).map(([k, v]) => (
                <span key={k} className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[k] || 'bg-gray-100 text-gray-600'}`}>{v} {k}</span>
              ))}
              <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">{s.totalWords.toLocaleString()} words</span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 font-medium flex items-center gap-1"><ImageIcon size={12} /> {s.totalImages} images</span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 font-medium flex items-center gap-1"><Film size={12} /> {s.totalVideos} videos</span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">{s.totalSpell} spelling flags</span>
            </div>
          )}

          {/* Readiness trend across audit runs (60→80→90) */}
          {batch.audit_runs && batch.audit_runs.length > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-100" data-testid="readiness-trend">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={14} className="text-indigo-500" />
                <span className="text-xs font-semibold text-gray-700">Readiness Trend (gate {batch.ready_gate || 85}% · benchmark 90%)</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {batch.audit_runs.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {i > 0 && <span className="text-gray-300">→</span>}
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${readinessColor(r.avg_readiness)}`} title={`${r.pct_ready}% of articles ≥ gate`}>
                      {r.label === 'ingest-baseline' ? 'Baseline' : `Run ${r.run_index}`}: {r.avg_readiness}%
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-2">
                Ingest is immutable — re-audit re-scores current article state + Brand Dictionary. Grow the dictionary &amp; run the Refine pass, then Re-Audit to move the score up.
              </p>
            </div>
          )}

          <div className="overflow-x-auto border border-gray-100 rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-50 text-left">
                <tr className="text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="px-3 py-2 font-medium text-center">OK</th>
                  <th className="px-3 py-2 font-medium text-center">Words</th>
                  <th className="px-3 py-2 font-medium text-center"><ImageIcon size={12} className="inline" /></th>
                  <th className="px-3 py-2 font-medium text-center"><Film size={12} className="inline" /></th>
                  <th className="px-3 py-2 font-medium text-center">Spell</th>
                  <th className="px-3 py-2 font-medium text-center">State</th>
                  <th className="px-3 py-2 font-medium text-center">Ready</th>
                  <th className="px-3 py-2 font-medium">Components</th>
                </tr>
              </thead>
              <tbody>
                {batch.entries.map((entry, i) => <EntryRow key={i} entry={entry} index={i} />)}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Ingested articles are saved as <b>drafts</b> with an immutable <code>raw_scrape</code>. Next: run the Refine pass per article (spellcheck + add missing components) before publishing.
          </p>
        </div>
      )}

      {/* Recent batches */}
      {batches.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2"><RefreshCw size={14} /> Recent Batches</h4>
            <button onClick={fetchBatches} className="text-xs text-indigo-600 hover:text-indigo-700">Refresh</button>
          </div>
          <div className="space-y-1">
            {batches.map((b) => (
              <button
                key={b.id}
                onClick={() => startPolling(b.id)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 text-left"
                data-testid={`batch-history-${b.batch_id}`}
              >
                <span className="text-sm text-gray-800">{b.batch_id}</span>
                <span className="text-xs text-gray-500">{b.processed}/{b.total} · {b.status}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchIngestLane;
